import {
  AfterViewInit,
  Component,
  DestroyRef,
  OnDestroy,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatLabel, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatError } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CustomersService } from '../../services/customers.service';
import { CustomerModel } from '../../customer.model';
import { CommonModule } from '@angular/common';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { CustomerSettingsFormComponent } from '../customer-settings-form/customer-settings-form.component';
import { splitEmails, joinEmails, EMAIL_PATTERN } from '../../../../shared/utils/email.util';

export type CustomerDetailsFormType = FormGroup<{
  contactFirstName: FormControl<string | null>;
  contactLastName: FormControl<string | null>;
  title: FormControl<string | null>;
  displayName: FormControl<string | null>;
  companyName: FormControl<string | null>;
  companyAccountNumber: FormControl<string | null>;
  businessPhone: FormControl<string | null>;
  businessPhoneExt: FormControl<string | null>;
  mobilePhoneNumber: FormControl<string | null>;
  /** First/primary email (shown in Business Details on the sectioned layout). */
  primaryEmail: FormControl<string | null>;
  /** Canonical `;`-delimited list (primary first, then additional contact emails). */
  contactEmail: FormControl<string | null>;
  notes: FormControl<string | null>;
  customerAddress: FormControl<string | null>;
  customerCity: FormControl<string | null>;
  customerState: FormControl<string | null>;
  customerZip: FormControl<string | null>;
  salesPersonId: FormControl<string | null>;
}>;

@Component({
  selector: 'app-customer-details-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatLabel,
    MatFormFieldModule,
    MatInputModule,
    MatError,
    MatSelectModule,
    MatOptionModule,
    MatChipsModule,
    MatIcon,
    MatButtonModule,
    CommonModule,
    NgxMaskDirective,
    CustomerSettingsFormComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './customer-details-form.component.html',
  styles: [
    `
      .email-input-row { display: flex; align-items: flex-start; gap: 0.5rem; }
      .email-input-row .email-input-field { flex: 1; }
      .email-input-row .add-email-btn { margin-top: 0.35rem; white-space: nowrap; }
      .email-error { color: #c62828; font-size: 0.85rem; }
      .email-chip-list { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.25rem; }
    `,
  ],
  host: { class: 'app-customer-details-form' },
})
export class CustomerDetailsFormComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild(CustomerSettingsFormComponent, { static: false }) settingsFormComponent?: CustomerSettingsFormComponent;
  errorMessages = {
    contactFirstName: {
      required: 'First name is required',
      minlength: 'First name must be at least 2 characters',
      maxlength: 'First name cannot exceed 40 characters',
      pattern: 'First name can only contain letters and spaces',
    },
    contactLastName: {
      required: 'Last name is required',
      minlength: 'Last name must be at least 2 characters',
      maxlength: 'Last name cannot exceed 40 characters',
      pattern: 'Last name can only contain letters and spaces',
    },
    companyName: {
      required: 'Customer name is required',
      minlength: 'Customer name must be at least 2 characters',
      maxlength: 'Customer name cannot exceed 40 characters',
    },
    displayName: {
      required: 'Display name is required',
    },
    companyAccountNumber: {
      required: 'Account number is required',
      pattern: 'Account number can only contain letters, numbers, and hyphens',
    },
    businessPhone: {
      pattern: 'Invalid phone number',
    },
    businessPhoneExt: {
      pattern: 'Extension can only contain up to 10 digits',
    },
    mobilePhoneNumber: {
      pattern: 'Please enter a valid mobile number',
    },
    contactEmail: {
      //email: 'Please enter a valid email address',
      pattern: 'Please enter a valid email address',
    },
    notes: {
      maxlength: 'Notes cannot exceed 800 characters',
    },
    customerAddress: {
      maxlength: 'Address cannot exceed 255 characters',
    },
    customerCity: {
      maxlength: 'City cannot exceed 100 characters',
    },
    customerZip: {
      pattern: 'Please enter a valid ZIP code (12345 or 12345-6789)',
    },
  };

  /** Labels used when reporting which fields are blocking a submit. */
  private readonly fieldLabels: Record<string, string> = {
    contactFirstName: 'Contact First Name',
    contactLastName: 'Contact Last Name',
    title: 'Title',
    displayName: 'Display Name',
    companyName: 'Customer Name',
    companyAccountNumber: 'Account Number',
    businessPhone: 'Business Phone',
    businessPhoneExt: 'Ext.',
    mobilePhoneNumber: 'Mobile Phone Number',
    primaryEmail: 'Primary Email Address',
    contactEmail: 'Email Address',
    notes: 'Notes',
    customerAddress: 'Address',
    customerCity: 'City',
    customerState: 'State',
    customerZip: 'ZIP Code',
  };

  usStates = [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
  ];

  @Input() data?: {
    data: {
      customerId: string;
      customerData?: CustomerModel;
    };
  };

  @Input() hideNotes = false;
  /** Hide the Account Number field (e.g. self-onboard, where it is supplied by the request). */
  @Input() hideAccountNumber = false;
  /**
   * 'default' renders the original Business Information / Contact Person / Address
   * grouping (used in admin). 'sections' renders the self-onboard "Business Details"
   * and "Contact Information" sections with the Title field and split primary/contact
   * email, and omits the inline payment-settings block.
   */
  @Input() layout: 'default' | 'sections' = 'default';
  /** Whether the customer already has a default payment method — enables autopay toggle when true. */
  @Input() hasDefaultPaymentMethod = false;
  /** Optional explicit feature flags for unauthenticated contexts (e.g. self-onboard public page). */
  @Input() enableAutopayFeature?: boolean;
  @Input() enableInvoiceFeature?: boolean;
  @Input() enableCustomerOverrideFeature?: boolean;
  @Input() enableAchFeature?: boolean;

  // Default customer settings for new customers (both enabled)
  defaultCustomerSettings = {
    acceptCreditCardPayment: true,
    acceptACHPayment: true,
  };

  form: CustomerDetailsFormType = new FormGroup({
    contactFirstName: new FormControl('', [
      //Validators.required,
      Validators.minLength(2),
      Validators.maxLength(40),
      Validators.pattern(/^[A-Za-z\s]+$/),
    ]),
    contactLastName: new FormControl('', [
      // Validators.required,
      Validators.minLength(2),
      Validators.maxLength(40),
      Validators.pattern(/^[A-Za-z\s]+$/),
    ]),
    title: new FormControl('', [Validators.maxLength(100)]),
    displayName: new FormControl('', [Validators.required]),
    companyName: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(40)]),
    companyAccountNumber: new FormControl('', [Validators.required, Validators.pattern(/^[A-Za-z0-9-]+$/)]),
    businessPhone: new FormControl('', [Validators.pattern(/^\(\d{3}\)\s\d{3}-\d{4}$/)]),
    businessPhoneExt: new FormControl('', [Validators.pattern(/^\d{0,10}$/)]),
    mobilePhoneNumber: new FormControl('', [Validators.pattern(/^\(\d{3}\)\s\d{3}-\d{4}$/)]),
    primaryEmail: new FormControl('', [Validators.pattern(EMAIL_PATTERN)]),
    // Holds the canonical `;`-delimited string; individual addresses are validated
    // by the chip UI as they are added (see addEmail).
    contactEmail: new FormControl(''),
    notes: new FormControl('', [Validators.maxLength(800)]),
    customerAddress: new FormControl('', [Validators.maxLength(255)]),
    customerCity: new FormControl('', [Validators.maxLength(100)]),
    customerState: new FormControl(''),
    customerZip: new FormControl('', [Validators.pattern(/^\d{5}(-\d{4})?$/)]),
    salesPersonId: new FormControl(''),
  });

  /** Chip list of the customer's email addresses (UI representation of contactEmail). */
  contactEmails: string[] = [];
  emailError: string | null = null;

  constructor(protected customersApiService: CustomersService, protected destroyRef: DestroyRef) {}

  /** Add the typed address as a chip after validating and de-duplicating it. */
  addEmail(input: HTMLInputElement): void {
    const email = input.value.trim();
    this.emailError = null;
    if (!email) return;

    if (!EMAIL_PATTERN.test(email)) {
      this.emailError = this.errorMessages.contactEmail.pattern;
      return;
    }
    if (this.contactEmails.some((e) => e.toLowerCase() === email.toLowerCase())) {
      this.emailError = 'This email has already been added';
      return;
    }

    this.contactEmails.push(email);
    input.value = '';
    this.syncContactEmail();
  }

  removeEmail(index: number): void {
    this.contactEmails.splice(index, 1);
    this.syncContactEmail();
  }

  /**
   * Push the primary email + chip list back into the canonical `contactEmail`
   * control as a `;`-delimited string (primary first). In the default layout the
   * primaryEmail control is empty, so this is just the chip list as before.
   */
  private syncContactEmail(): void {
    const primary = (this.form.controls.primaryEmail.value || '').trim();
    const all = [primary, ...this.contactEmails].filter(Boolean);
    this.form.controls.contactEmail.setValue(joinEmails(all));
    this.form.controls.contactEmail.markAsDirty();
  }

  /** Re-sync the canonical contactEmail when the primary email changes (sections layout). */
  onPrimaryEmailChange(): void {
    this.syncContactEmail();
  }

  /**
   * Populate the primary email + additional-email chips from a canonical
   * `;`-delimited string (first address = primary, rest = contact emails).
   * Used by the self-onboard page to hydrate the split email fields.
   */
  setEmails(combined?: string | null): void {
    const list = splitEmails(combined || '');
    this.form.controls.primaryEmail.setValue(list[0] || '');
    this.contactEmails = list.slice(1);
    this.syncContactEmail();
  }

  ngAfterViewInit(): void {
    if (this.data?.data.customerData) {
      const customerData = this.data.data.customerData;
      this.contactEmails = splitEmails(customerData.contactEmail);

      // Format phone number if it exists
      const [formattedPhone, formattedPhoneExt] = customerData.contactPhoneNumber
        ? this.formatBusinessPhoneNumber(customerData.contactPhoneNumber)
        : '';

      // Update all form fields with customer data
      this.form.patchValue({
        contactFirstName: customerData.contactFirstName,
        contactLastName: customerData.contactLastName,
        title: customerData.title || '',
        displayName: customerData.displayName || '',
        companyName: customerData.companyName || '',
        companyAccountNumber: customerData.companyAccountNumber || '',
        businessPhone: formattedPhone,
        businessPhoneExt: formattedPhoneExt || '',
        mobilePhoneNumber: this.formatPhoneNumber(customerData.mobilePhoneNumber || ''),
        contactEmail: customerData.contactEmail || undefined,
        notes: customerData.notes || '',
        customerAddress: customerData.customerAddress || '',
        customerCity: customerData.customerCity || '',
        customerState: customerData.customerState || '',
        customerZip: customerData.customerZip || '',
        salesPersonId: customerData.salesPersonId || '',
      });
    }
  }

  ngOnDestroy(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data?.data.customerData) {
      const customerData = this.data.data.customerData;
    }
  }

  onCompanyNameBlur(): void {
    // if change is to company name, and displayName is empty, we need to update the displayName
    const companyName = this.form.get('companyName')?.value;
    if (companyName && !this.form.get('displayName')?.value) {
      this.form.patchValue({ displayName: companyName }, { emitEvent: false });
    }
  }

  /**
   * Split a stored phone number into the 10 digits the `(000) 000-0000` mask expects
   * plus any trailing extension digits. A leading US country code is dropped first,
   * otherwise an 11-digit number shifts by one and yields a mangled area code.
   */
  private splitPhoneDigits(phoneNumber: string): [string, string] {
    let digitsOnly = phoneNumber.replace(/\D/g, '');
    if (digitsOnly.length > 10 && digitsOnly.startsWith('1')) {
      digitsOnly = digitsOnly.slice(1);
    }
    return [digitsOnly.slice(0, 10), digitsOnly.slice(10)];
  }

  /** Format a stored `contactPhoneNumber` into the masked number and its extension. */
  formatBusinessPhoneNumber(phoneNumber: string): [string, string] {
    const [national, extension] = this.splitPhoneDigits(phoneNumber);
    return [national.replace(/^(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3'), extension];
  }

  /**
   * Format a stored phone number that carries no extension (e.g. a mobile number).
   * Values shorter than 10 digits are returned as-is so they fail validation
   * visibly rather than being silently reshaped into something plausible.
   */
  formatPhoneNumber(phoneNumber: string): string {
    return this.formatBusinessPhoneNumber(phoneNumber)[0];
  }

  getCustomerSettings() {
    return this.settingsFormComponent?.getFormValue();
  }

  /** Labels of every enabled control currently failing validation. */
  getInvalidFieldLabels(): string[] {
    return Object.keys(this.form.controls)
      .filter((name) => this.form.get(name)?.invalid)
      .map((name) => this.fieldLabels[name] ?? name);
  }

  /**
   * Mark already-invalid controls as touched so Material renders their errors.
   * Values hydrated with patchValue leave controls untouched, so a bad stored
   * value would otherwise fail validation with nothing shown on screen.
   */
  revealInvalidFields(): void {
    Object.keys(this.form.controls).forEach((name) => {
      const control = this.form.get(name);
      if (control?.invalid) {
        control.markAsTouched();
      }
    });
  }
}
